import { useMemo, useState } from "react";
import type { MemeItem } from "@/mocks/homeData";
import SectionHeader from "./SectionHeader";

interface MemeGalleryProps {
  id: string;
  eyebrow: string;
  title: string;
  desc: string;
  items: MemeItem[];
  onOpen: (item: MemeItem) => void;
  accent?: "primary" | "accent" | "secondary";
  bg?: string;
}

const accentText: Record<string, string> = {
  primary: "text-primary-500",
  accent: "text-accent-600",
  secondary: "text-secondary-600",
};

const accentBorder: Record<string, string> = {
  primary: "hover:border-primary-300",
  accent: "hover:border-accent-300",
  secondary: "hover:border-secondary-300",
};

export default function MemeGallery({
  id,
  eyebrow,
  title,
  desc,
  items,
  onOpen,
  accent = "primary",
  bg = "bg-background-50",
}: MemeGalleryProps) {
  const tabs = useMemo(() => {
    const set = new Set(items.map((i) => i.tag));
    return ["全部", ...Array.from(set)];
  }, [items]);

  const [active, setActive] = useState("全部");
  const visible = active === "全部" ? items : items.filter((i) => i.tag === active);

  return (
    <section id={id} className={`${bg} py-14 md:py-20`}>
      <div className="mx-auto max-w-[1200px] px-4 md:px-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeader eyebrow={eyebrow} title={title} desc={desc} />
          {/* Tab 切换 */}
          <div className="inline-flex w-full max-w-full overflow-x-auto rounded-full bg-background-100 p-1 lg:w-auto">
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setActive(t)}
                className={`flex-1 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors lg:flex-none cursor-pointer ${
                  active === t
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {visible.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onOpen(item)}
              className={`group relative overflow-hidden rounded-2xl border border-background-200/70 bg-background-100 text-left transition-all hover:-translate-y-1 ${accentBorder[accent]} cursor-pointer`}
              aria-label={`查看${item.title}`}
            >
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={item.img}
                  alt={item.alt}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span
                  className={`absolute left-2.5 top-2.5 rounded-full bg-background-50/90 px-2.5 py-0.5 text-[11px] font-semibold ${accentText[accent]}`}
                >
                  {item.tag}
                </span>
                <span className="absolute right-2.5 top-2.5 flex h-7 w-7 items-center justify-center rounded-full bg-foreground-950/45 text-background-50 opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
                  <i className="ri-zoom-in-line text-sm" />
                </span>
              </div>
              <div className="p-3">
                <h4 className="truncate text-sm font-semibold text-foreground-950">{item.title}</h4>
                <p className="mt-1 truncate text-xs text-foreground-500">{item.caption}</p>
                <div className="mt-2 flex items-center gap-1 text-[11px] text-foreground-400">
                  <i className="ri-fire-line" />
                  {item.hot > 0 ? `${(item.hot / 1000).toFixed(1)}k 热度` : "新素材"}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}